import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import { KpiCard } from "@/components/Cards";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Layers, Inbox, Timer, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
    Submitted: "bg-gold-500/10 text-gold-700 border-gold-200 border",
    InProgress: "bg-blue-50 text-blue-700 border-blue-200 border",
    InForce: "bg-emerald-50 text-emerald-700 border-emerald-200 border",
    Closed: "bg-emerald-50 text-emerald-700 border-emerald-200 border",
    Expired: "bg-navy-50 text-navy-500 border-navy-200 border",
    Declined: "bg-red-50 text-red-700 border-red-200 border",
};

const STATUS_OPTIONS = ["Submitted", "InProgress", "InForce", "Expired", "Declined", "Closed"];

export default function AdminApplications() {
    const [apps, setApps] = useState([]);

    const load = () => api.get("/admin/applications").then((r) => setApps(r.data));

    useEffect(() => {
        load();
    }, []);

    const stats = useMemo(() => {
        const completed = new Set(["InForce", "Closed"]);
        return {
            total: apps.length,
            submitted: apps.filter((a) => a.application_status === "Submitted").length,
            in_progress: apps.filter((a) => a.application_status === "InProgress").length,
            completed: apps.filter((a) => completed.has(a.application_status)).length,
        };
    }, [apps]);

    const setStatus = async (id, application_status) => {
        try {
            await api.patch(`/admin/applications/${id}`, { application_status });
            toast.success(`Application marked ${application_status}`);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <>
            <PageHeader
                eyebrow="Oversight"
                title="All applications"
                description="Every calculator quote submitted across every vendor on Perx."
                testid="admin-apps-header"
            />

            <div className="px-6 lg:px-12 py-8 space-y-6">
                <div className="grid sm:grid-cols-4 gap-5">
                    <KpiCard label="Total" value={stats.total} icon={Layers} />
                    <KpiCard label="Submitted" value={stats.submitted} icon={Inbox} accent="pink" />
                    <KpiCard label="In progress" value={stats.in_progress} icon={Timer} />
                    <KpiCard label="Completed" value={stats.completed} icon={ShieldCheck} />
                </div>

                <div className="rounded-2xl bg-white border border-navy-100/70 shadow-soft overflow-hidden">
                    <Table data-testid="admin-apps-table">
                        <TableHeader>
                            <TableRow className="bg-cream hover:bg-cream">
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Member
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Product
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Vendor
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Submitted
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Status
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Notes
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {apps.map((a) => (
                                <TableRow key={a.id} data-testid={`admin-app-row-${a.id}`}>
                                    <TableCell>
                                        <div className="text-navy-700 font-medium">
                                            {a.member?.name || "Household member"}
                                        </div>
                                        <div className="text-xs text-navy-500">
                                            {a.member?.email || a.buyer_email}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-navy-700">{a.product_name}</TableCell>
                                    <TableCell className="text-navy-500">{a.vendor || "—"}</TableCell>
                                    <TableCell className="text-navy-500">
                                        {a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}
                                    </TableCell>
                                    <TableCell>
                                        <Select
                                            value={a.application_status}
                                            onValueChange={(v) => setStatus(a.id, v)}
                                        >
                                            <SelectTrigger
                                                data-testid={`admin-app-status-${a.id}`}
                                                className="h-9 w-[140px] bg-white border-navy-100"
                                            >
                                                <SelectValue>
                                                    <Badge className={STATUS_STYLE[a.application_status] || ""}>
                                                        {a.application_status}
                                                    </Badge>
                                                </SelectValue>
                                            </SelectTrigger>
                                            <SelectContent className="bg-white">
                                                {STATUS_OPTIONS.map((s) => (
                                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </TableCell>
                                    <TableCell className="text-navy-500 max-w-xs">
                                        {a.notes || "—"}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </>
    );
}
